
/* eslint-disable no-underscore-dangle */

import { mapGetters, mapActions } from 'vuex';

// API
import * as apiGeo from '../api/public/geo';
import * as apiGeonames from '../api/geonames/geo';
// Models
import Location from '../models/location';
// Libraries
import * as apiLoc from '../libraries/geolocation';
import { getDisplayPlaceName } from '../libraries/utils';
import { addMarker, makeCluster } from '../libraries/carto';

import errorsMixin from '../mixins/error-handler';

export default {
  name: 'SResultMap',
  mixins: [errorsMixin],
  props: {
    mobileLayout: {
      type: Boolean,
      required: true,
    },
    listPlaygrounds: {
      type: Array,
      required: true,
    },
    center: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      indexList: -1,
      playground: null,
      currentMap: null,
      lastSearchCenter: [],
      lastSearchZoom: null,
      waitEndPanTo: false,
      waitFitBounds: false,
      swiperIndex: 0,
      mapToken: null,
      loadingMap: true,
      timeoutMapToken: null,
      mapErrorMessage: null,
      refreshEnabled: false,
      autoMove: true,
      userLocation: false,
    };
  },
  computed: {
    ...mapGetters({
      getMapToken: 'map/getMapToken',
      getMapTokenExp: 'map/getMapTokenExp',
      getWindowWidth: 'screen/getWindowWidth',
    }),
    mapCenter() {
      const center = [];
      if (this.center) {
        center.push(this.center.lon);
        center.push(this.center.lat);
      } else {
        center.push(7.266962);
        center.push(47.654779);
      }
      return center;
    },
    mapOptions() {
      return {
        style: `${process.env.VUE_APP_MAP_URL}styles/osm-bright/style.json`,
        zoom: 13,
        minZoom: 11,
        maxZoom: 17,
        center: this.mapCenter,
      };
    },
    showSearchBtn() {
      if (this.currentMap && this.refreshEnabled) {
        const bound = this.currentMap.getBounds();
        const latDelta = (bound._ne.lat - bound._sw.lat) / 2;
        const lngDelta = (bound._ne.lng - bound._sw.lng) / 2;

        const startLat = this.lastSearchCenter[0];
        const startLng = this.lastSearchCenter[1];
        const endLat = this.currentMap.getCenter().lat;
        const endLng = this.currentMap.getCenter().lng;

        const showSearchMove = (Math.abs(startLat - endLat) > latDelta || Math.abs(startLng - endLng) > lngDelta);
        const showSearchZoom = this.currentMap.getZoom() < this.lastSearchZoom;

        return showSearchMove || showSearchZoom;
      }
      return false;
    },
    showFitBounds() {
      if (this.currentMap && !this.waitFitBounds && !this.waitEndPanTo) {
        const bound = this.currentMap.getBounds();
        const mapMinLat = bound._sw.lat;
        const mapMaxLat = bound._ne.lat;
        const mapMinLon = bound._sw.lng;
        const mapMaxLon = bound._ne.lng;

        const {
          minLat, maxLat, minLon, maxLon,
        } = this.getPlaygroundsBounds;

        const isInLatRange = mapMinLat <= minLat && maxLat <= mapMaxLat;
        const isInLonRange = mapMinLon <= minLon && maxLon <= mapMaxLon;
        return !isInLatRange || !isInLonRange;
      }
      return false;
    },
    getPlaygroundsBounds() {
      if (this.listPlaygrounds.length) {
        let minLat = 90;
        let maxLat = -90;
        let minLon = 180;
        let maxLon = -180;
        this.listPlaygrounds.forEach((playground) => {
          if (playground.location.lat < minLat) minLat = playground.location.lat;
          if (playground.location.lat > maxLat) maxLat = playground.location.lat;
          if (playground.location.lon < minLon) minLon = playground.location.lon;
          if (playground.location.lon > maxLon) maxLon = playground.location.lon;
        });
        return {
          minLat, maxLat, minLon, maxLon,
        };
      }
      return {
        minLat: this.mapCenter[1],
        maxLat: this.mapCenter[1],
        minLon: this.mapCenter[0],
        maxLon: this.mapCenter[0],
      };
    },
  },
  watch: {
    listPlaygrounds: {
      handler() {
        if (this.currentMap) {
          this.loadMap();
          this.fitBounds();
        }
      },
      deep: true,
    },
    indexList() {
      if (this.currentMap.getSource('current-src')) {
        this.currentMap.removeLayer('current-layer');
        this.currentMap.removeSource('current-src');
      }
      if (this.indexList !== -1) {
        this.playground = this.listPlaygrounds[this.indexList];
        addMarker(this.currentMap, {
          lat: this.playground.location.lat,
          lng: this.playground.location.lon,
          icon: {
            label: 'current',
            url: '/img/map/current.png',
            width: 42,
            height: 42,
            left: 0,
            top: 0.5,
          },
        });
        this.swiperIndex = this.listPlaygrounds.findIndex((playground) => playground.id === this.playground.id);
      } else {
        this.playground = null;
      }
    },
    swiperIndex() {
      if (this.swiperIndex < 0 || this.swiperIndex > this.listPlaygrounds.length - 1) {
        this.swiperIndex = 0;
      }
      this.indexList = this.swiperIndex;
      this.currentMap.panTo([this.listPlaygrounds[this.indexList].location.lon, this.listPlaygrounds[this.indexList].location.lat]);
    },
  },
  mounted() {
    if (!this.getMapToken || this.getMapTokenExp < Date.now()) {
      this.callMapToken();
    } else {
      this.mapToken = this.getMapToken;
      const timeLeft = this.getMapTokenExp - Date.now() - 5000;
      if (timeLeft <= 0) {
        this.callMapToken();
      } else {
        clearTimeout(this.timeoutMapToken);
        this.timeoutMapToken = setTimeout(() => {
          this.callMapToken();
        }, timeLeft);
      }
    }
  },
  beforeDestroy() {
    clearTimeout(this.timeoutMapToken);
  },
  methods: {
    ...mapActions({
      initSnackbar: 'snackbar/initSnackbar',
      hideSnackbar: 'snackbar/hideSnackbar',
      updateCurrentSearch: 'transferData/updateCurrentSearch',
      setMapToken: 'map/setMapToken',
    }),

    refresh() {
      this.autoMove = false;

      this.searchMap();
    },
    callMapToken() {
      apiGeo.mapToken(this).then((result) => {
        this.mapToken = result.access_token;
        this.setMapToken({ token: result.access_token, exp: result.expires_in });

        const timeLeft = this.getMapTokenExp - Date.now();
        clearTimeout(this.timeoutMapToken);
        this.timeoutMapToken = setTimeout(() => {
          this.callMapToken();
        }, timeLeft);
      // Empty catch, error will be generated by mapbox if token is invalid
      }).catch(() => null);
    },
    moveEnd() {
      if (this.waitEndPanTo || this.waitFitBounds) {
        if (this.waitEndPanTo) {
          this.waitEndPanTo = false;
          this.searchMap();
        }

        this.setLastSearch();
        this.waitFitBounds = false;
        this.refreshEnabled = true;
      }
    },
    changeIndex(id) {
      this.indexList = this.listPlaygrounds.findIndex(
        (playground) => playground.id === id,
      );
    },
    mapLoaded(map) {
      this.currentMap = map;
      this.loadMap();
      this.fitBounds();
    },
    geoLocation(wakeUp) {
      apiLoc.geolocation(true, this).then((data) => {
        this.errors$.reset('geo');
        this.hideSnackbar();
        const { lon, lat } = data.location;
        this.currentMap.setZoom(13);
        this.currentMap.panTo([lon, lat]);
        this.refreshEnabled = false;
        this.autoMove = false;
        this.waitEndPanTo = true;
        this.userLocation = true;
      }).catch((error) => {
        const permissionDenied = error.code === 'permission_denied';
        const snackbar = {
          options: {
            color: 'error',
            timeout: wakeUp || permissionDenied ? -1 : 5000, // Use -1 to keep open indefinitely
            clearable: wakeUp || permissionDenied,
          },
          content: this.errors$.get('geo', 'position'),
        };
        this.initSnackbar(snackbar);
      });
    },
    searchMap() {
      this.refreshEnabled = false;

      const center = this.currentMap.getCenter();
      this.errors$.reset();
      this.errors$.handleError(center.lat < -90 || center.lat > 90 || center.lng < -180 || center.lng > 180, 'geo', 'position');

      if (this.errors$.hasErrors()) {
        const snackbar = {
          options: {
            color: 'error',
            timeout: -1, // Use -1 to keep open indefinitely
            clearable: true,
          },
          content: this.errors$.get('geo', 'position'),
        };
        this.initSnackbar(snackbar);
        return;
      }
      if (center.lat !== this.$route.params.lat || center.lng !== this.$route.params.lon) {
        apiGeonames.reverse({ lat: center.lat, lon: center.lng }, null).then((res) => {
          res.result.location.lat = center.lat;
          res.result.location.lon = center.lng;
          // Type 3 "city" by default
          res.result.type = 3;
          // Search by geographical coordinates
          res.result.geoSearch = true;
          // Geolocation of the user set when method geoLocation is called
          if (this.userLocation) {
            // Reset before the next search which may not be by user's geolocation
            this.userLocation = false;
            res.result.userLocation = true;
          }

          const location = new Location(res.result);
          this.updateCurrentSearch(location);

          const place = location.city.alias;
          const { lat, lon } = location.location;
          const admin = location.admin ? location.admin.alias : location.country.alias;
          const userLocation = location.userLocation;

          if (this.$matomo) {
            this.$matomo.trackEvent('Search', 'mapSearch', `${place} - lat: ${lat}, lon: ${lon}`);
          }
          this.$newRouter.replace({
            name: 'list-city',
            params: {
              admin, place, userLocation,
            },
            query: { lat, lon },
          });
        // Empty catch, keep the current search
        }).catch(() => null);
      }
    },
    loadMap() {
      this.clearMap();
      this.indexList = -1;

      if (this.listPlaygrounds.length) {
        this.initSnackbar({
          options: {
            color: 'info',
          },
          content: `${this.listPlaygrounds.length} résultat${this.listPlaygrounds.length > 1 ? 's' : ''}`,
        });

        const points = [];
        this.listPlaygrounds.forEach((playground) => {
          const cityName = getDisplayPlaceName(playground.city, playground.sub);
          points.push({
            coords: [playground.location.lon, playground.location.lat],
            label: `${cityName}, ${playground.street}`,
            id: playground.id,
          });
        });

        if (points.length > 0 && !this.currentMap.getSource('cluster')) {
          makeCluster(this.currentMap, points, {
            clusterRadius: 50,
            icon: {
              label: 'default',
              url: '/img/map/gmap.png',
              width: 42,
              height: 42,
              left: 0,
              top: 0.5,
            },
          });
        }
      }
    },
    setLastSearch() {
      this.lastSearchCenter = [];
      this.lastSearchCenter.push(this.currentMap.getCenter().lat);
      this.lastSearchCenter.push(this.currentMap.getCenter().lng);
      this.lastSearchZoom = this.currentMap.getZoom();
    },
    fitBounds() {
      if (!this.listPlaygrounds.length || !this.autoMove) {
        this.setLastSearch();
        this.refreshEnabled = true;
        this.autoMove = true;
        return;
      }

      const {
        minLat, maxLat, minLon, maxLon,
      } = this.getPlaygroundsBounds;

      this.currentMap.fitBounds([[minLon, minLat], [maxLon, maxLat]], {
        padding: {
          top: 50, bottom: 50, left: 50, right: 50,
        },
      });
      this.waitFitBounds = true;
    },
    clearMap() {
      if (this.currentMap.getSource('cluster')) {
        this.currentMap.removeLayer('cluster-outline');
        this.currentMap.removeLayer('cluster-circles');
        this.currentMap.removeLayer('cluster-markers');
        this.currentMap.removeLayer('cluster-count');
        this.currentMap.removeSource('cluster');
      }
    },
    mapError(map, e) {
      if (/^40[0-9]$/.test(e.error.status)) {
        this.mapErrorMessage = e.error.message;
        this.mapToken = null;
        this.loadingMap = false;
        clearTimeout(this.timeoutMapToken);
        this.setMapToken({ token: null, exp: null });
      }
    },
  },
};
